import { createPortal } from "react-dom";

import type { DisplayLoyaltyTier } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";
import { DraggableModal } from "atoms/draggable-modal/DraggableModal";
import { Button } from "atoms/button/Button";
import { formatCurrency } from "toolbox/Money";

import { LoyaltyTierBadge } from "./LoyaltyTierBadge";
import type { EnabledLoyaltyStatus } from "./useLoyaltyStatus";

type HowItWorksSheetProps = {
  open: boolean;
  status: EnabledLoyaltyStatus;
  onClose: () => void;
};

type Translate = ReturnType<typeof useTranslation>["t"];

/** "3 visits", or "From day one" for the base tier every client starts on. */
const qualifiesAtCopy = (t: Translate, tier: DisplayLoyaltyTier) =>
  tier.qualifyAtVisits === 0
    ? t("loyalty.qualifyFromJoining")
    : t("loyalty.qualifyVisits", { count: tier.qualifyAtVisits });

/**
 * The program explained: every tier on the client's ladder (their home clinic's) with its threshold
 * and public description, then the rules. Portalled to
 * the body so the header popover cannot trap the fixed sheet, inside its own `app` query container
 * (as the popover has) so `desk:` styles still make it a centred dialog, stacked above the popover.
 */
export const HowItWorksSheet = ({ open, status, onClose }: HowItWorksSheetProps) => {
  const { t } = useTranslation();
  const { rules, visitCashValueInCents } = status;

  return createPortal(
    <div
      style={{ containerType: "inline-size", containerName: "app" }}
      className="pointer-events-none fixed inset-0 z-[60] [&>*:first-child]:pointer-events-auto"
    >
      <DraggableModal
        open={open}
        onClose={onClose}
        header={
          <h2 className="flex w-full items-center justify-center border-b border-light-grey pb-5 pt-6 font-sans text-bold2 uppercase tracking-[.04em] text-primary">
            {t("loyalty.howItWorksTitle")}
          </h2>
        }
        footer={
          <div className="px-5 py-4">
            <Button fullWidth onClick={onClose}>
              {t("loyalty.gotIt")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-5 pb-2 text-left text-primary">
          {status.currentTier && (
            <div className="flex flex-col items-center gap-2 pt-5">
              <LoyaltyTierBadge tier={status.currentTier} size="lg" />
              <span className="text-body1 font-semibold">{status.currentTier.name}</span>
            </div>
          )}
          <ul
            aria-label={t("loyalty.tiersTitle")}
            className="flex flex-col divide-y divide-light-grey"
          >
            {status.ladder.map(({ tier }) => (
              <li key={tier.id} aria-label={tier.name} className="flex items-start gap-3 py-4">
                <LoyaltyTierBadge tier={tier} size="md" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-body1">{tier.name}</span>
                    {status.currentTier?.id === tier.id && (
                      <span className="rounded-full bg-background-two px-2 py-0.5 text-body3 text-secondary">
                        {t("loyalty.youAreHere")}
                      </span>
                    )}
                  </div>
                  <p className="text-body2 text-secondary">{qualifiesAtCopy(t, tier)}</p>
                  {tier.description && (
                    <p className="text-body2 text-primary">{tier.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-2 rounded-xl bg-background-two p-4">
            <p className="text-bold2 text-primary">{t("loyalty.rulesTitle")}</p>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-body2 text-secondary">
              <li>{t("loyalty.ruleMetricVisits")}</li>
              {typeof visitCashValueInCents === "number" && (
                <li>
                  {t("loyalty.ruleSpendCountsAsVisit", {
                    amount: formatCurrency(visitCashValueInCents, 0),
                  })}
                </li>
              )}
              {rules.topTierIsLifetime && <li>{t("loyalty.ruleTopTierLifetime")}</li>}
            </ul>
          </div>
        </div>
      </DraggableModal>
    </div>,
    document.body,
  );
};
